import { useState, MouseEvent, useEffect } from 'react';

import classNames from 'classnames';
import Skeleton from '@mui/material/Skeleton';
import Fade from '@mui/material/Fade';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import { useLocation } from 'react-router';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { useIsDesktop, useTextOverflow } from '@vk-hr-tek/ui/hooks';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';

import { useSelector } from '@app/hooks';

import { selectStatus } from '../../../../user';
import {
  ProcessedChildMenuItem,
  ProcessedCustomDropdownItem,
  ProcessedSystemDropdownItem,
  SidebarMenuItemProps,
} from '../../../types';
import { TIMEOUT_FOR_FADE } from '../../../constants';
import { SidebarContext, useSidebarContext } from '../SidebarContext';
import { SidebarMappedItem } from '../SidebarMappedItem';

import { useStyles } from './SidebarDropdownItem.styles';

const TIMEOUT_FOR_SUBMENU_HOVER = 50;

type SidebarDropdownItemProps =
  | SidebarMenuItemProps<ProcessedCustomDropdownItem['type']>
  | SidebarMenuItemProps<ProcessedSystemDropdownItem['type']>;

export const SidebarDropdownItem = ({
  item,
  isInDrop = false,
}: SidebarDropdownItemProps) => {
  const {
    sidebarOpened,
    isActive,
    onMenuItemClick,
    setSubmenusVisibleItemsCount,
  } = useSidebarContext();

  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();
  const isSkeletonLoading = useSelector(selectStatus) === 'loading';

  const { ref, isOverflowed, checkOverflow } = useTextOverflow();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasSidebarClosed, setHasSidebarClosed] = useState(!sidebarOpened);

  const { classes } = useStyles({
    expanded,
    sidebarOpened,
  });

  const { title, iconName, children } = item;

  const hasActiveChild = (items: ProcessedChildMenuItem[]) => {
    const haveActiveChild = items.some((child) => {
      if (child.type === 'custom_link_item' || child.type === 'link_item') {
        return child.href === pathname;
      }

      if (child.children) {
        return hasActiveChild(child.children);
      }
    });

    return haveActiveChild;
  };

  useEffect(() => {
    if (!sidebarOpened) {
      setExpanded(false);
    }
  }, [sidebarOpened]);

  const handleOpenMenu = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const hoverOnMenuItem = !sidebarOpened ? handleOpenMenu : undefined;

  const handleExpandInnerMenu = () => {
    setExpanded((prev) => !prev);

    if (!expanded) {
      setSubmenusVisibleItemsCount?.(children.length);
    } else {
      setSubmenusVisibleItemsCount?.(-children.length);
    }
  };

  const handleCloseHover = () => {
    setTimeout(() => {
      handleCloseMenu();
    }, TIMEOUT_FOR_SUBMENU_HOVER);
  };

  const open = Boolean(anchorEl);

  const renderChildren = () => (
    <SidebarContext
      value={{
        sidebarOpened,
        isActive,
        onMenuItemClick,
      }}
    >
      {children.map((child) => (
        <SidebarMappedItem
          key={child.id}
          item={child}
          showIcon={false}
          isInDoubleDrop={isInDrop}
          isInDrop
        />
      ))}
    </SidebarContext>
  );

  const isInDropAndClosedSidebar = !sidebarOpened && isInDrop;
  const isTooltipVisible = isOverflowed && sidebarOpened;
  const isTextWrapped = hasSidebarClosed && isInDropAndClosedSidebar;

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="relative"
      onMouseEnter={hoverOnMenuItem}
      onMouseLeave={handleCloseHover}
    >
      <Tooltip
        maxWidth="310px"
        title={isTooltipVisible ? title : ''}
        placement="right"
        disabled={!isDesktop}
      >
        <Box
          className={classNames(
            classes.menuItem,
            hasActiveChild(children) && !sidebarOpened && classes.activeParent,
          )}
          aria-owns={anchorEl ? 'simple-menu' : undefined}
          aria-haspopup="true"
          onClick={sidebarOpened ? handleExpandInnerMenu : hoverOnMenuItem}
          onMouseEnter={sidebarOpened ? checkOverflow : undefined}
          minWidth={40}
          minHeight={40}
        >
          {!isInDropAndClosedSidebar && (
            <Box
              minWidth={24}
              minHeight={24}
              visibility={isInDrop ? 'hidden' : 'visible'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              className="aqa_menuItemIcon"
            >
              {isSkeletonLoading ? (
                <Skeleton width="100%" />
              ) : (
                <Icon name={iconName} size="20" color="text.light.primary" />
              )}
            </Box>
          )}

          {!sidebarOpened && !isInDrop && (
            <Fade in={!sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
              <Box className={classes.absoluteIcon}>
                <Icon name="dropdown_outline" size="12" />
              </Box>
            </Fade>
          )}

          <Fade
            in={isInDrop || sidebarOpened}
            timeout={TIMEOUT_FOR_FADE}
            appear={false}
            onExited={() => setHasSidebarClosed(true)}
            onEnter={() => setHasSidebarClosed(false)}
          >
            <Box
              flex="1"
              minWidth={0}
              pl={isInDropAndClosedSidebar ? '8' : '16'}
            >
              <Typography ref={ref} variant="body3" noWrap={!isTextWrapped}>
                {isSkeletonLoading ? <Skeleton width={200} /> : title}
              </Typography>
            </Box>
          </Fade>

          {(sidebarOpened || isInDrop) && (
            <Box className={classes.menuItemExpandIcon}>
              <Icon
                name={sidebarOpened ? 'dropdown' : 'chevron_right'}
                size="16"
              />
            </Box>
          )}
        </Box>
      </Tooltip>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box display="flex" flexDirection="column">
          {renderChildren()}
        </Box>
      </Collapse>

      {!sidebarOpened && isDesktop && (
        <Menu
          id="simple-menu"
          className={classNames(classes.submenu)}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          slotProps={{
            root: {
              disableAutoFocus: true,
              disableEnforceFocus: true,
              disableRestoreFocus: true,
            },
            list: {
              autoFocusItem: false,
            },
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PopoverClasses={{
            root: classes.popOverRoot,
          }}
        >
          <Box className={classes.menuItemTitleContainer}>
            <Typography variant="caption" color="text.light.tertirary">
              {title}
            </Typography>
          </Box>

          {renderChildren()}
        </Menu>
      )}
    </Box>
  );
};
